<template>
  <div class="property-editor">
    <div class="property-row" :style="{ marginLeft: level * 20 + 'px' }">
      <div class="property-main">
        <el-input 
          v-model="localProperty.name" 
          placeholder="Property name"
          size="small"
          class="property-name"
          @change="updateProperty"
        />
        
        <el-select 
          v-model="localProperty.type" 
          size="small"
          class="property-type"
          @change="onTypeChange"
        >
          <el-option label="String" value="string"></el-option>
          <el-option label="Number" value="number"></el-option>
          <el-option label="Boolean" value="boolean"></el-option>
          <el-option label="Object" value="object"></el-option>
          <el-option label="Array" value="array"></el-option>
          <el-option label="Date" value="date"></el-option>
        </el-select>
        
        <el-button 
          v-if="localProperty.type === 'array'"
          size="small"
          type="text"
          @click="showArrayEditor = !showArrayEditor"
          class="array-config-btn"
        >
          <i :class="showArrayEditor ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
          {{ getArrayConfigText() }}
        </el-button>
        
        <el-checkbox 
          v-model="localProperty.required" 
          size="small"
          @change="updateProperty"
        >
          Required
        </el-checkbox>
        
        <el-popover
          v-if="canImport"
          placement="bottom"
          width="400"
          trigger="click"
        >
          <div class="import-panel">
            <h4>Import Structure from Existing Data Object</h4>
            <p class="import-description">
              Import the structure of an existing data object to reuse its properties without creating duplicates.
            </p>
            <el-select 
              v-model="selectedImportObject" 
              placeholder="Choose a data object to import from..."
              clearable
              size="small"
              style="width: 100%; margin-bottom: 10px;"
              filterable
              @change="confirmImport"
            >
              <el-option
                v-for="obj in dataObjects"
                :key="obj._id"
                :label="`${obj.name} (${obj.type})`"
                :value="obj._id"
              >
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <div>
                    <div style="font-weight: 500;">{{ obj.name }}</div>
                    <div style="font-size: 12px; color: #909399;">{{ obj.description || 'No description' }}</div>
                  </div>
                  <el-tag size="mini" :type="obj.type === 'ARRAY' ? 'warning' : 'primary'">
                    {{ obj.type }}
                  </el-tag>
                </div>
              </el-option>
            </el-select>
            <div class="import-note">
              <i class="el-icon-info"></i>
              <span>This will replace the current structure of this property</span>
            </div>
          </div>
          
          <el-button slot="reference" size="mini" type="primary" plain class="import-button">
            <i class="el-icon-download"></i> Import Structure
          </el-button>
        </el-popover>
        
        <el-popover
          v-if="showValueRestriction"
          placement="bottom"
          width="400"
          trigger="click"
        >
          <div class="value-restriction-panel">
            <h4>Value Restrictions</h4>
            <el-radio-group v-model="localProperty.restrictionType" @change="onRestrictionTypeChange">
              <el-radio label="none">No Restriction</el-radio>
              <el-radio label="list">From List</el-radio>
              <el-radio label="range">Range</el-radio>
              <el-radio label="regex">Regular Expression</el-radio>
            </el-radio-group>
            
            <div v-if="localProperty.restrictionType === 'list'" class="restriction-config">
              <h5>Allowed Values</h5>
              <div v-for="(value, idx) in localProperty.restrictionValues" :key="idx" class="list-value-item">
                <el-input v-model="localProperty.restrictionValues[idx]" size="small" placeholder="Enter value"/>
                <el-button size="mini" type="text" @click="removeListValue(idx)">
                  <i class="el-icon-delete"></i>
                </el-button>
              </div>
              <el-button size="mini" @click="addListValue">
                <i class="el-icon-plus"></i> Add Value
              </el-button>
            </div>
            
            <div v-if="localProperty.restrictionType === 'range' && localProperty.type === 'number'" class="restriction-config">
              <h5>Number Range</h5>
              <div class="range-inputs">
                <el-input-number v-model="localProperty.restrictionRange.min" :precision="2" size="small" placeholder="Min"/>
                <span>to</span>
                <el-input-number v-model="localProperty.restrictionRange.max" :precision="2" size="small" placeholder="Max"/>
              </div>
            </div>
            
            <div v-if="localProperty.restrictionType === 'range' && localProperty.type === 'string'" class="restriction-config">
              <h5>String Length Range</h5>
              <div class="range-inputs">
                <el-input-number v-model="localProperty.restrictionRange.minLength" :min="0" size="small" placeholder="Min Length"/>
                <span>to</span>
                <el-input-number v-model="localProperty.restrictionRange.maxLength" :min="0" size="small" placeholder="Max Length"/>
              </div>
            </div>
            
            <div v-if="localProperty.restrictionType === 'regex'" class="restriction-config">
              <h5>Regular Expression Pattern</h5>
              <el-input v-model="localProperty.restrictionPattern" size="small" placeholder="Enter regex pattern"/>
              <small>Example: ^[A-Z][a-z]+$</small>
            </div>
            
            <div class="restriction-actions">
              <el-button size="small" type="primary" @click="applyRestrictions">Apply</el-button>
            </div>
          </div>
          
          <el-button slot="reference" size="mini" type="text" :class="hasRestriction ? 'has-restriction' : ''">
            <i class="el-icon-lock"></i> Restrictions
          </el-button>
        </el-popover>
        
        <div class="property-actions">
          <el-button 
            v-if="canHaveChildren"
            size="mini" 
            type="text"
            @click="toggleExpanded"
            class="expand-btn"
          >
            <i :class="expanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
          </el-button>
          
          <el-button 
            v-if="canHaveChildren"
            size="mini" 
            type="text"
            @click="addChild"
            class="add-child-btn"
          >
            <i class="el-icon-plus"></i>
          </el-button>
          
          <el-button 
            size="mini" 
            type="text"
            @click="deleteProperty"
            class="delete-btn"
          >
            <i class="el-icon-delete"></i>
          </el-button>
        </div>
      </div>
      
      <el-input 
        v-model="localProperty.description" 
        placeholder="Description (optional)"
        size="small"
        class="property-description"
        @change="updateProperty"
      />
    </div>
    
    <!-- Array Items Editor -->
    <div v-if="showArrayEditor && localProperty.type === 'array'" class="array-items-editor">
      <div class="array-items-header">
        <h5>Array Elements:</h5>
        <p class="array-description">
          Define multiple element types that can be included in this array.
        </p>
      </div>
      
      <div class="array-items-list">
        <div 
          v-for="(item, index) in localProperty.arrayItems" 
          :key="`array-item-${index}`"
          class="array-item-card"
        >
          <div class="array-item-header">
            <div class="item-info">
              <span class="item-index">#{{ index + 1 }}</span>
              <el-input 
                v-model="item.name" 
                placeholder="Element name"
                size="small"
                class="item-name"
                @change="updateArrayItems"
              />
              <el-select 
                v-model="item.type" 
                size="small" 
                class="item-type"
                @change="onPropertyArrayItemTypeChange(index)"
              >
                <el-option label="String" value="string"></el-option>
                <el-option label="Number" value="number"></el-option>
                <el-option label="Boolean" value="boolean"></el-option>
                <el-option label="Object" value="object"></el-option>
                <el-option label="Array" value="array"></el-option>
                <el-option label="Date" value="date"></el-option>
              </el-select>
            </div>
            <div class="item-actions">
              <el-button 
                v-if="item.type && item.type.toLowerCase() === 'object'"
                size="mini" 
                type="primary"
                icon="el-icon-download"
                @click="showPropertyArrayItemImportDialog(index)"
                class="import-from-button"
              >
                Import
              </el-button>
              <el-button 
                size="mini" 
                type="danger" 
                @click="removePropertyArrayItem(index)"
                icon="el-icon-delete"
              >
                Delete
              </el-button>
            </div>
          </div>
          
          <div class="item-description">
            <el-input 
              v-model="item.description" 
              placeholder="Description for this element type"
              size="small"
              @change="updateArrayItems"
            />
          </div>
          
          <div v-if="item.type === 'object'" class="array-object-properties">
            <div class="object-properties-header">
              <h6>Object Properties:</h6>
            </div>
            <property-editor
              v-for="(prop, propIndex) in item.properties"
              :key="`${prop.id}-${index}`"
              :property="prop"
              :index="propIndex"
              :level="level + 2"
              :parent-type="'OBJECT'"
              :data-objects="dataObjects"
              @update="(id, property) => updatePropertyArrayItemProperty(index, { id, property })"
              @delete="(id) => deletePropertyArrayItemProperty(index, id)"
              @add-child="(parentId) => addPropertyArrayItemChildProperty(index, parentId)"
              @import-structure="(propertyId, dataObjectId) => importPropertyArrayItemPropertyStructure(index, propertyId, dataObjectId)"
            />
            <el-button size="mini" @click="addPropertyArrayItemProperty(index)">
              <i class="el-icon-plus"></i> Add Property
            </el-button>
          </div>
        </div>
        
        <div v-if="!localProperty.arrayItems || localProperty.arrayItems.length === 0" class="no-array-items">
          <p>No array elements defined.</p>
        </div>
      </div>
      
      <div class="array-actions">
        <el-button size="small" type="primary" @click="addPropertyArrayItem">
          <i class="el-icon-plus"></i> Add Array Element Type
        </el-button>
      </div>
    </div>
    
    <div v-if="expanded && canHaveChildren" class="property-children" :key="childrenKey">
      <property-editor
        v-for="(child, childIndex) in localProperty.children"
        :key="`${child.id}-${childIndex}-${childrenKey}`"
        :property="child"
        :index="childIndex"
        :level="level + 1"
        :parent-type="childParentType"
        :data-objects="dataObjects"
        @update="updateChild"
        @delete="deleteChild"
        @add-child="addGrandchild"
        @import-structure="passImportStructure"
      />
      
      <div v-if="(!localProperty.children || localProperty.children.length === 0)" 
           class="no-properties"
           :style="{ marginLeft: (level + 1) * 20 + 'px' }">
        <span>No properties defined</span>
        <el-button size="mini" type="text" @click="addChild">
          <i class="el-icon-plus"></i> Add property
        </el-button>
      </div>
    </div>
    
    <!-- Array Item Import Dialog -->
    <el-dialog 
      title="Import Data Object Structure" 
      :visible.sync="arrayItemImportDialogVisible"
      width="50%"
    >
      <div class="import-dialog-content">
        <p class="import-dialog-description">
          Select a data object to import its structure into this array element:
        </p>
        <el-select 
          v-model="selectedArrayItemImportObject" 
          placeholder="Choose a data object..."
          clearable
          size="medium"
          style="width: 100%;"
          filterable
        >
          <el-option
            v-for="obj in dataObjects.filter(o => o.type === 'OBJECT')"
            :key="obj._id"
            :label="obj.name"
            :value="obj._id"
          >
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div>
                <div style="font-weight: 500;">{{ obj.name }}</div>
                <div style="font-size: 12px; color: #909399;">{{ obj.description || 'No description' }}</div>
              </div>
              <el-tag size="mini" type="primary">OBJECT</el-tag>
            </div>
          </el-option>
        </el-select>
        <div class="import-warning">
          <i class="el-icon-warning"></i>
          <span>This will replace the current element's property structure.</span>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelArrayItemImport">Cancel</el-button>
        <el-button type="primary" @click="confirmArrayItemImportDialog" :disabled="!selectedArrayItemImportObject">
          Import
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'PropertyEditor',
  props: {
    property: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    parentType: {
      type: String,
      default: 'OBJECT'
    },
    level: {
      type: Number,
      default: 0
    },
    dataObjects: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localProperty: this.initializeProperty(this.property),
      expanded: false,
      childrenKey: 0,
      selectedImportObject: null,
      showArrayEditor: false,
      propertyIdCounter: 1000, // Start high to avoid conflicts
      arrayItemImportDialogVisible: false,
      selectedArrayItemImportObject: null,
      currentArrayItemIndex: null
    }
  },
  computed: {
    canHaveChildren() {
      return this.localProperty.type === 'object' || 
             (this.localProperty.type === 'array' && this.localProperty.itemType === 'object') ||
             (this.localProperty.type === 'array' && this.localProperty.arrayItems && 
              this.localProperty.arrayItems.some(item => item.type === 'object'))
    },
    childParentType() {
      if (this.localProperty.type === 'object') {
        return 'OBJECT'
      }
      if (this.localProperty.type === 'array' && this.localProperty.itemType === 'object') {
        return 'ARRAY_OBJECT'
      }
      return this.parentType
    },
    showValueRestriction() {
      return ['string', 'number'].includes(this.localProperty.type)
    },
    hasRestriction() {
      return this.localProperty.restrictionType && this.localProperty.restrictionType !== 'none'
    },
    canImport() {
      return (this.localProperty.type === 'object' || 
              (this.localProperty.type === 'array' && this.localProperty.itemType === 'object')) &&
              this.dataObjects && this.dataObjects.length > 0
    },
    
    arrayItemsCount() {
      return this.localProperty.arrayItems ? this.localProperty.arrayItems.length : 0
    }
  },
  watch: {
    property: {
      handler(newVal) {
        this.localProperty = this.initializeProperty(newVal)
        this.childrenKey++
      },
      deep: true
    }
  },
  methods: {
    initializeProperty(prop) {
      const cloned = JSON.parse(JSON.stringify(prop))
      if (!cloned.restrictionType) {
        cloned.restrictionType = 'none'
      }
      if (!cloned.restrictionValues) {
        cloned.restrictionValues = []
      }
      if (!cloned.restrictionRange) {
        cloned.restrictionRange = {}
      }
      if (!cloned.restrictionPattern) {
        cloned.restrictionPattern = ''
      }
      if (!cloned.arrayItems) {
        cloned.arrayItems = []
        // Migrate from old single itemType structure if present
        if (cloned.type === 'array' && cloned.itemType) {
          const migratedItem = {
            id: ++this.propertyIdCounter,
            name: `${cloned.itemType} element`,
            type: cloned.itemType,
            description: '',
            properties: cloned.itemType === 'object' ? (cloned.children || []) : []
          }
          cloned.arrayItems = [migratedItem]
        }
      }
      return cloned
    },
    updateProperty() {
      this.$emit('update', this.localProperty.id, this.localProperty)
    },
    
    deleteProperty() {
      this.$emit('delete', this.localProperty.id)
    },
    
    onTypeChange() {
      if (this.localProperty.type === 'array') {
        // Initialize itemType if not present
        if (!this.localProperty.itemType) {
          this.$set(this.localProperty, 'itemType', 'string')
        }
        if (!this.localProperty.children) {
          this.$set(this.localProperty, 'children', [])
        }
      } else if (this.localProperty.type === 'object') {
        if (!this.localProperty.children) {
          this.$set(this.localProperty, 'children', [])
        }
        // Remove itemType when changing away from array
        if (this.localProperty.itemType) {
          this.$delete(this.localProperty, 'itemType')
        }
      } else {
        // Clean up for non-container types
        if (this.localProperty.children) {
          this.$delete(this.localProperty, 'children')
        }
        if (this.localProperty.itemType) {
          this.$delete(this.localProperty, 'itemType')
        }
      }
      this.updateProperty()
    },
    
    onArrayItemTypeChange() {
      // Ensure proper reactivity when changing array item type
      if (this.localProperty.itemType === 'object') {
        if (!this.localProperty.children) {
          this.$set(this.localProperty, 'children', [])
        }
      } else {
        // Clear children for non-object array types
        this.$set(this.localProperty, 'children', [])
      }
      this.updateProperty()
    },
    
    getArrayConfigText() {
      const count = this.arrayItemsCount
      if (count === 0) {
        return 'Configure Array Elements'
      } else if (count === 1) {
        const item = this.localProperty.arrayItems[0]
        return `1 element (${item.type})`
      } else {
        return `${count} elements`
      }
    },
    
    addPropertyArrayItem() {
      if (!this.localProperty.arrayItems) {
        this.$set(this.localProperty, 'arrayItems', [])
      }
      this.localProperty.arrayItems.push({
        id: ++this.propertyIdCounter,
        name: '',
        type: 'string',
        description: '',
        properties: []
      })
      this.updateArrayItems()
    },
    
    removePropertyArrayItem(index) {
      this.localProperty.arrayItems.splice(index, 1)
      this.updateArrayItems()
    },
    
    updateArrayItems() {
      this.updateProperty()
      // Force parent component to refresh its computed properties
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    
    onPropertyArrayItemTypeChange(index) {
      const item = this.localProperty.arrayItems[index]
      if (item.type === 'object') {
        if (!item.properties) {
          this.$set(item, 'properties', [])
        }
      } else {
        this.$set(item, 'properties', [])
      }
      this.updateArrayItems()
    },
    
    addPropertyArrayItemProperty(itemIndex) {
      const arrayItem = this.localProperty.arrayItems[itemIndex]
      if (!arrayItem.properties) {
        this.$set(arrayItem, 'properties', [])
      }
      arrayItem.properties.push({
        id: ++this.propertyIdCounter,
        name: '',
        type: 'string',
        description: '',
        required: false,
        children: [],
        itemType: null,
        restrictionType: 'none',
        restrictionValues: [],
        restrictionRange: {},
        restrictionPattern: ''
      })
      this.updateArrayItems()
    },
    
    updatePropertyArrayItemProperty(itemIndex, updateData) {
      if (typeof updateData === 'object' && updateData.id && updateData.property) {
        const { id, property } = updateData
        const updateInTree = (props) => {
          for (let i = 0; i < props.length; i++) {
            if (props[i].id === id) {
              props[i] = { ...props[i], ...property }
              return true
            }
            if (props[i].children && updateInTree(props[i].children)) {
              return true
            }
          }
          return false
        }
        const arrayItem = this.localProperty.arrayItems[itemIndex]
        if (arrayItem && arrayItem.properties) {
          updateInTree(arrayItem.properties)
          this.updateArrayItems()
        }
      }
    },
    
    deletePropertyArrayItemProperty(itemIndex, id) {
      const deleteFromTree = (props) => {
        for (let i = 0; i < props.length; i++) {
          if (props[i].id === id) {
            props.splice(i, 1)
            return true
          }
          if (props[i].children && deleteFromTree(props[i].children)) {
            return true
          }
        }
        return false
      }
      const arrayItem = this.localProperty.arrayItems[itemIndex]
      if (arrayItem && arrayItem.properties) {
        deleteFromTree(arrayItem.properties)
        this.updateArrayItems()
      }
    },
    
    addPropertyArrayItemChildProperty(itemIndex, parentId) {
      const addToParent = (props) => {
        for (let prop of props) {
          if (prop.id === parentId) {
            if (!prop.children) {
              prop.children = []
            }
            prop.children.push({
              id: ++this.propertyIdCounter,
              name: '',
              type: 'string',
              description: '',
              required: false,
              children: [],
              itemType: null,
              restrictionType: 'none',
              restrictionValues: [],
              restrictionRange: {},
              restrictionPattern: ''
            })
            return true
          }
          if (prop.children && addToParent(prop.children)) {
            return true
          }
        }
        return false
      }
      const arrayItem = this.localProperty.arrayItems[itemIndex]
      if (arrayItem && arrayItem.properties) {
        addToParent(arrayItem.properties)
        this.updateArrayItems()
      }
    },
    
    importPropertyArrayItemPropertyStructure(itemIndex, propertyId, dataObjectId) {
      // This would implement structure importing for nested properties
      // Similar to the implementation in DataObjectManagement.vue
      this.$emit('import-structure', propertyId, dataObjectId)
    },
    
    toggleExpanded() {
      this.expanded = !this.expanded
    },
    
    addChild() {
      if (!this.localProperty.children) {
        this.localProperty.children = []
      }
      this.$emit('add-child', this.localProperty.id)
      this.expanded = true
    },
    
    updateChild(id, childProperty) {
      const childIndex = this.localProperty.children.findIndex(c => c.id === id)
      if (childIndex !== -1) {
        this.localProperty.children[childIndex] = childProperty
        this.childrenKey++
        this.updateProperty()
      }
    },
    
    deleteChild(id) {
      const childIndex = this.localProperty.children.findIndex(c => c.id === id)
      if (childIndex !== -1) {
        this.localProperty.children.splice(childIndex, 1)
        this.childrenKey++
        this.updateProperty()
      }
    },
    
    addGrandchild(parentId) {
      this.$emit('add-child', parentId)
      this.childrenKey++
    },
    
    onRestrictionTypeChange() {
      if (this.localProperty.restrictionType === 'list' && !this.localProperty.restrictionValues.length) {
        this.localProperty.restrictionValues = ['']
      }
      if (this.localProperty.restrictionType === 'range') {
        if (this.localProperty.type === 'number') {
          this.localProperty.restrictionRange = { min: 0, max: 100 }
        } else if (this.localProperty.type === 'string') {
          this.localProperty.restrictionRange = { minLength: 0, maxLength: 255 }
        }
      }
    },
    
    addListValue() {
      this.localProperty.restrictionValues.push('')
    },
    
    removeListValue(index) {
      this.localProperty.restrictionValues.splice(index, 1)
    },
    
    applyRestrictions() {
      this.updateProperty()
      this.$message.success('Restrictions applied')
    },
    
    confirmImport() {
      if (!this.selectedImportObject) return
      
      const selectedObj = this.dataObjects.find(obj => obj._id === this.selectedImportObject)
      if (!selectedObj) return
      
      this.$confirm(
        `Import structure from "${selectedObj.name}"? This will replace the current structure.`,
        'Confirm Import',
        {
          confirmButtonText: 'Import',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      ).then(() => {
        this.$emit('import-structure', this.localProperty.id, this.selectedImportObject)
        this.selectedImportObject = null
      }).catch(() => {
        this.selectedImportObject = null
      })
    },
    
    passImportStructure(propertyId, dataObjectId) {
      this.$emit('import-structure', propertyId, dataObjectId)
    },
    
    showPropertyArrayItemImportDialog(itemIndex) {
      this.currentArrayItemIndex = itemIndex
      this.selectedArrayItemImportObject = null
      this.arrayItemImportDialogVisible = true
    },
    
    cancelArrayItemImport() {
      this.arrayItemImportDialogVisible = false
      this.selectedArrayItemImportObject = null
      this.currentArrayItemIndex = null
    },
    
    confirmArrayItemImportDialog() {
      if (!this.selectedArrayItemImportObject || this.currentArrayItemIndex === null) return
      
      const selectedObj = this.dataObjects.find(obj => obj._id === this.selectedArrayItemImportObject)
      if (!selectedObj) return
      
      // Import the structure from the selected object
      const arrayItem = this.localProperty.arrayItems[this.currentArrayItemIndex]
      if (selectedObj.type === 'OBJECT' && selectedObj.properties && arrayItem) {
        this.$set(arrayItem, 'properties', JSON.parse(JSON.stringify(selectedObj.properties)))
        this.assignPropertyIds(arrayItem.properties)
        // Ensure the array item type remains as object
        this.$set(arrayItem, 'type', 'object')
        this.updateArrayItems()
        this.$message.success(`Imported structure from "${selectedObj.name}"`)
      }
      
      // Close dialog and reset
      this.arrayItemImportDialogVisible = false
      this.selectedArrayItemImportObject = null
      this.currentArrayItemIndex = null
    },
    
    assignPropertyIds(properties) {
      properties.forEach(prop => {
        if (!prop.id) {
          prop.id = ++this.propertyIdCounter
        }
        if (prop.children) {
          this.assignPropertyIds(prop.children)
        }
      })
    }
  }
}
</script>

<style scoped>
.property-editor {
  margin-bottom: 10px;
}

.property-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  margin-bottom: 5px;
}

.property-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.property-name {
  width: 180px;
}

.property-type {
  width: 120px;
}

.array-item-type {
  width: 120px;
}

.property-description {
  width: 100%;
}

.property-actions {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.expand-btn,
.add-child-btn,
.delete-btn {
  padding: 5px 8px;
}

.expand-btn:hover,
.add-child-btn:hover {
  color: #409EFF;
}

.delete-btn:hover {
  color: #F56C6C;
}

.property-children {
  margin-top: 10px;
  padding-left: 20px;
  border-left: 2px solid #e4e7ed;
}

.no-properties {
  padding: 10px;
  color: #909399;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.no-properties .el-button {
  color: #409EFF;
}

.has-restriction {
  color: #E6A23C !important;
}

.value-restriction-panel {
  padding: 10px;
}

.value-restriction-panel h4 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 14px;
}

.value-restriction-panel h5 {
  margin: 15px 0 10px 0;
  color: #606266;
  font-size: 13px;
}

.restriction-config {
  margin-top: 15px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
}

.list-value-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.range-inputs {
  display: flex;
  align-items: center;
  gap: 10px;
}

.range-inputs span {
  color: #909399;
  font-size: 14px;
}

.restriction-actions {
  margin-top: 15px;
  text-align: right;
  padding-top: 10px;
  border-top: 1px solid #e4e7ed;
}

.restriction-config small {
  display: block;
  color: #909399;
  margin-top: 5px;
  font-size: 12px;
}

.import-panel {
  padding: 15px;
}

.import-panel h4 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.import-description {
  margin: 0 0 15px 0;
  color: #606266;
  font-size: 13px;
  line-height: 1.4;
}

.import-note {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #E6A23C;
  font-size: 12px;
  background: #FDF6EC;
  padding: 8px;
  border-radius: 4px;
  border-left: 3px solid #E6A23C;
}

.import-button {
  background: linear-gradient(135deg, #409EFF 0%, #36A3F7 100%);
  border: none;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.2);
}

.import-button:hover {
  background: linear-gradient(135deg, #66B1FF 0%, #409EFF 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(64, 158, 255, 0.3);
}

.import-button i {
  margin-right: 4px;
}

.array-config-btn {
  color: #409EFF;
  font-weight: 500;
}

.array-config-btn:hover {
  color: #66B1FF;
}

.array-items-editor {
  margin-top: 10px;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

.array-items-header h5 {
  margin: 0 0 5px 0;
  color: #495057;
  font-size: 14px;
  font-weight: 600;
}

.array-description {
  margin: 0 0 12px 0;
  color: #6c757d;
  font-size: 12px;
}

.array-item-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
}

.array-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.item-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.item-index {
  background: #6c757d;
  color: white;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
}

.item-name {
  width: 140px;
}

.item-type {
  width: 100px;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.item-description {
  margin-bottom: 8px;
}

.array-object-properties {
  background: #f1f3f4;
  border-radius: 4px;
  padding: 8px;
  border: 1px solid #e0e0e0;
}

.object-properties-header h6 {
  margin: 0 0 8px 0;
  color: #495057;
  font-size: 12px;
  font-weight: 600;
}

.no-array-items {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-size: 12px;
}

.array-actions {
  text-align: center;
  padding-top: 8px;
  border-top: 1px solid #dee2e6;
}

.import-from-button {
  margin-right: 4px;
  font-weight: 500;
  font-size: 12px;
  padding: 4px 8px;
  transition: all 0.3s ease;
}

.import-from-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

.import-dialog-content {
  padding: 10px;
}

.import-dialog-description {
  margin: 0 0 20px 0;
  color: #606266;
  font-size: 14px;
}

.import-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding: 12px;
  background: #FDF6EC;
  border-radius: 4px;
  border-left: 3px solid #E6A23C;
  color: #E6A23C;
  font-size: 13px;
}

.import-warning i {
  font-size: 16px;
}
</style>